/**
 * Binance public market data — endpoints, wire types, and REST fetchers.
 *
 * None of this needs an API key. Everything used here is on the public
 * market-data surface, which is CORS-enabled.
 *
 * Endpoint choice matters more than it looks. `data-api.binance.vision` is
 * Binance's dedicated public market-data host: no auth surface, generous CORS,
 * and it is not subject to the geo-blocking that intermittently affects
 * api.binance.com. We try it first and fall back through the numbered API
 * hosts, because a single blocked host should degrade to "slower first paint",
 * not "no chart".
 */

export const REST_HOSTS = [
  'https://data-api.binance.vision',
  'https://api.binance.com',
  'https://api-gcp.binance.com',
  'https://api1.binance.com',
  'https://api2.binance.com',
  'https://api3.binance.com',
] as const;

export const WS_HOSTS = [
  'wss://data-stream.binance.vision',
  'wss://stream.binance.com:9443',
  'wss://stream.binance.com:443',
] as const;

/** Binance caps a single connection at 1024 streams; we shard well below it. */
export const MAX_STREAMS_PER_SOCKET = 180;

export type Interval =
  | '1m'
  | '3m'
  | '5m'
  | '15m'
  | '30m'
  | '1h'
  | '2h'
  | '4h'
  | '6h'
  | '8h'
  | '12h'
  | '1d'
  | '3d'
  | '1w';

export const INTERVALS: Interval[] = [
  '1m',
  '3m',
  '5m',
  '15m',
  '30m',
  '1h',
  '2h',
  '4h',
  '6h',
  '8h',
  '12h',
  '1d',
  '3d',
  '1w',
];

const MINUTE = 60_000;
export const INTERVAL_MS: Record<Interval, number> = {
  '1m': MINUTE,
  '3m': 3 * MINUTE,
  '5m': 5 * MINUTE,
  '15m': 15 * MINUTE,
  '30m': 30 * MINUTE,
  '1h': 60 * MINUTE,
  '2h': 120 * MINUTE,
  '4h': 240 * MINUTE,
  '6h': 360 * MINUTE,
  '8h': 480 * MINUTE,
  '12h': 720 * MINUTE,
  '1d': 1440 * MINUTE,
  '3d': 4320 * MINUTE,
  '1w': 10080 * MINUTE,
};

// ---------------------------------------------------------------------------
// Wire types (what actually arrives on the socket)
// ---------------------------------------------------------------------------

/** Combined-stream envelope: /stream?streams=a/b/c */
export interface StreamEnvelope {
  stream: string;
  data: unknown;
}

export interface WsTrade {
  e: 'trade';
  E: number; // event time
  s: string; // symbol
  t: number; // trade id
  p: string; // price
  q: string; // quantity
  T: number; // trade time
  m: boolean; // is the BUYER the maker? true => aggressive SELL
}

export interface WsAggTrade {
  e: 'aggTrade';
  E: number;
  s: string;
  a: number;
  p: string;
  q: string;
  f: number;
  l: number;
  T: number;
  m: boolean;
}

/** Diff depth update. Levels are [price, qty]; qty "0" means remove. */
export interface WsDepth {
  e: 'depthUpdate';
  E: number;
  s: string;
  U: number; // first update id in event
  u: number; // final update id in event
  b: [string, string][];
  a: [string, string][];
}

export interface WsKline {
  e: 'kline';
  E: number;
  s: string;
  k: {
    t: number; // open time
    T: number; // close time
    s: string;
    i: string;
    o: string;
    c: string;
    h: string;
    l: string;
    v: string; // base volume
    n: number; // trade count
    x: boolean; // is this kline closed?
    q: string; // quote volume
    V: string; // taker buy base volume
    Q: string; // taker buy quote volume
  };
}

/** 24hr rolling window ticker. */
export interface WsTicker {
  e: '24hrTicker';
  E: number;
  s: string;
  p: string; // price change
  P: string; // price change percent
  c: string; // last price
  Q: string; // last qty
  b: string; // best bid
  a: string; // best ask
  o: string; // open
  h: string; // high
  l: string; // low
  v: string; // base volume
  q: string; // quote volume
  n: number; // trade count
}

export type RestKline = [
  number, // 0 open time
  string, // 1 open
  string, // 2 high
  string, // 3 low
  string, // 4 close
  string, // 5 volume
  number, // 6 close time
  string, // 7 quote volume
  number, // 8 trades
  string, // 9 taker buy base
  string, // 10 taker buy quote
  string, // 11 ignore
];

export interface RestDepth {
  lastUpdateId: number;
  bids: [string, string][];
  asks: [string, string][];
}

export interface RestTicker24h {
  symbol: string;
  priceChange: string;
  priceChangePercent: string;
  lastPrice: string;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  volume: string;
  quoteVolume: string;
  count: number;
}

export interface SymbolMeta {
  symbol: string;
  base: string;
  quote: string;
  pricePrecision: number;
  qtyPrecision: number;
  tickSize: number;
}

// ---------------------------------------------------------------------------
// Stream name builders
// ---------------------------------------------------------------------------

export const tradeStream = (s: string) => `${s.toLowerCase()}@trade`;
export const depthStream = (s: string) => `${s.toLowerCase()}@depth@100ms`;
export const klineStream = (s: string, i: Interval) => `${s.toLowerCase()}@kline_${i}`;
export const tickerStream = (s: string) => `${s.toLowerCase()}@ticker`;

// ---------------------------------------------------------------------------
// REST with host failover
// ---------------------------------------------------------------------------

/** Hosts that returned a network/CORS error, so we stop leading with them. */
const hostPenalty = new Map<string, number>();

function orderedHosts(): string[] {
  return [...REST_HOSTS].sort(
    (a, b) => (hostPenalty.get(a) ?? 0) - (hostPenalty.get(b) ?? 0),
  );
}

export class BinanceError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly code?: number,
  ) {
    super(message);
    this.name = 'BinanceError';
  }
}

async function restGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const hosts = orderedHosts();
  let lastErr: unknown;

  for (const host of hosts) {
    try {
      const res = await fetch(host + path, {
        signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(8000)]) : AbortSignal.timeout(8000),
        mode: 'cors',
        headers: { Accept: 'application/json' },
      });

      if (res.status === 429 || res.status === 418) {
        // Rate limited or banned on this host — try the next one immediately.
        hostPenalty.set(host, (hostPenalty.get(host) ?? 0) + 10);
        lastErr = new BinanceError(`rate limited on ${host}`, res.status);
        continue;
      }

      if (!res.ok) {
        let code: number | undefined;
        let msg = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { code?: number; msg?: string };
          code = body.code;
          if (body.msg) msg = body.msg;
        } catch {
          /* non-JSON error body */
        }
        // A 4xx from a valid host is a real API error, not a transport
        // problem — failing over would just repeat it.
        if (res.status >= 400 && res.status < 500 && res.status !== 403) {
          throw new BinanceError(msg, res.status, code);
        }
        hostPenalty.set(host, (hostPenalty.get(host) ?? 0) + 5);
        lastErr = new BinanceError(msg, res.status, code);
        continue;
      }

      // Success: decay this host's penalty so a transient blip is forgiven.
      const p = hostPenalty.get(host) ?? 0;
      if (p > 0) hostPenalty.set(host, Math.max(0, p - 1));
      return (await res.json()) as T;
    } catch (err) {
      if (err instanceof BinanceError && err.status && err.status < 500 && err.status !== 403) {
        throw err;
      }
      if (signal?.aborted) throw err;
      hostPenalty.set(host, (hostPenalty.get(host) ?? 0) + 3);
      lastErr = err;
    }
  }

  throw new BinanceError(
    `all Binance REST hosts failed: ${lastErr instanceof Error ? lastErr.message : String(lastErr)}`,
  );
}

// ---------------------------------------------------------------------------
// Clock synchronisation
// ---------------------------------------------------------------------------

/**
 * Offset between the exchange clock and this machine's, in milliseconds.
 *
 * This is not a nicety. Every event on the wire is stamped with Binance's
 * clock, and a desktop whose clock has drifted — several minutes is common on
 * machines that have been asleep — makes `Date.now() - eventTime` meaningless.
 * Left uncorrected it reports negative latency, shows "last tick" ages in the
 * future, and makes any staleness check either fire constantly or never.
 */
let clockOffset = 0;
let clockSynced = false;

/** Best estimate of the exchange's current time. */
export function serverNow(): number {
  return Date.now() + clockOffset;
}

export function clockSkew(): number {
  return clockOffset;
}

export function isClockSynced(): boolean {
  return clockSynced;
}

/**
 * Measure the offset with a round trip, assuming symmetric latency — the
 * standard estimate, and accurate to roughly half the round-trip time, which
 * is an order of magnitude better than the drift it corrects for.
 */
export async function syncClock(signal?: AbortSignal): Promise<number> {
  try {
    const t0 = Date.now();
    const res = await restGet<{ serverTime: number }>('/api/v3/time', signal);
    const t1 = Date.now();
    clockOffset = res.serverTime - (t0 + t1) / 2;
    clockSynced = true;
  } catch {
    // Leave the previous estimate in place rather than reverting to zero.
  }
  return clockOffset;
}

export function fetchKlines(
  symbol: string,
  interval: Interval,
  limit = 750,
  signal?: AbortSignal,
): Promise<RestKline[]> {
  const q = `?symbol=${symbol.toUpperCase()}&interval=${interval}&limit=${Math.min(1000, limit)}`;
  return restGet<RestKline[]>('/api/v3/klines' + q, signal);
}

/** Klines strictly older than `endTime`, for lazy history when panning left. */
export function fetchKlinesBefore(
  symbol: string,
  interval: Interval,
  endTime: number,
  limit = 500,
  signal?: AbortSignal,
): Promise<RestKline[]> {
  const q =
    `?symbol=${symbol.toUpperCase()}&interval=${interval}` +
    `&endTime=${Math.floor(endTime) - 1}&limit=${Math.min(1000, limit)}`;
  return restGet<RestKline[]>('/api/v3/klines' + q, signal);
}

export function fetchDepthSnapshot(
  symbol: string,
  limit: 100 | 500 | 1000 = 1000,
  signal?: AbortSignal,
): Promise<RestDepth> {
  return restGet<RestDepth>(
    `/api/v3/depth?symbol=${symbol.toUpperCase()}&limit=${limit}`,
    signal,
  );
}

export function fetchTickers(symbols: string[], signal?: AbortSignal): Promise<RestTicker24h[]> {
  if (symbols.length === 0) return Promise.resolve([]);
  const list = JSON.stringify(symbols.map((s) => s.toUpperCase()));
  return restGet<RestTicker24h[]>(
    `/api/v3/ticker/24hr?symbols=${encodeURIComponent(list)}`,
    signal,
  );
}

interface RawExchangeInfo {
  symbols: {
    symbol: string;
    status: string;
    baseAsset: string;
    quoteAsset: string;
    filters: { filterType: string; tickSize?: string; stepSize?: string }[];
  }[];
}

function decimalsOf(tick: string | undefined): number {
  if (!tick) return 2;
  const dot = tick.indexOf('.');
  if (dot < 0) return 0;
  let end = tick.length - 1;
  while (end > dot && tick.charCodeAt(end) === 48) end--;
  return Math.max(0, end - dot);
}

export async function fetchSymbolMeta(
  symbols: string[],
  signal?: AbortSignal,
): Promise<SymbolMeta[]> {
  if (symbols.length === 0) return [];
  const list = JSON.stringify(symbols.map((s) => s.toUpperCase()));
  const info = await restGet<RawExchangeInfo>(
    `/api/v3/exchangeInfo?symbols=${encodeURIComponent(list)}`,
    signal,
  );
  return info.symbols.map((s) => {
    const priceFilter = s.filters.find((f) => f.filterType === 'PRICE_FILTER');
    const lotFilter = s.filters.find((f) => f.filterType === 'LOT_SIZE');
    return {
      symbol: s.symbol,
      base: s.baseAsset,
      quote: s.quoteAsset,
      pricePrecision: decimalsOf(priceFilter?.tickSize),
      qtyPrecision: decimalsOf(lotFilter?.stepSize),
      tickSize: priceFilter?.tickSize ? parseFloat(priceFilter.tickSize) : 0.01,
    };
  });
}

/** All actively-trading spot symbols — used by the symbol search box. */
export async function fetchAllSymbols(signal?: AbortSignal): Promise<string[]> {
  const info = await restGet<RawExchangeInfo>('/api/v3/exchangeInfo?permissions=SPOT', signal);
  return info.symbols.filter((s) => s.status === 'TRADING').map((s) => s.symbol);
}
